import { Button, List, Form, Row, Space, Typography, Col } from 'antd';
import React from 'react';
import { renderInput } from '../renderInput';
import {
  CopyOutlined,
  DeleteOutlined,
  HolderOutlined
} from '@ant-design/icons';
import classes from './ListItem.module.css';
import Modal from 'app/components/Modal';
import type { Identifier, XYCoord } from 'dnd-core';
import { useDrop, useDrag } from 'react-dnd';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DefectItem = ({
  fields,
  config,
  name,
  onCopy,
  onDelete,
  onMove,
  index,
  readonly = false
}) => {
  const ref = React.useRef<any>(null);
  const dropRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const [, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: 'box',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });
  const [{ ...collected }, drag, dragPreview] = useDrag({
    type: 'box',
    item: () => {
      return { id: index, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: !readonly
  });
  // const opacity = collected.isDragging ? 0 : 1;

  const showWarningPopup = (e: any) => {
    e.preventDefault();
    Modal.danger({
      type: 'confirm',
      title: 'Are sure you want to delete ?',
      content: 'Warning! Current data has not been saved yet.',
      onOk: () => {
        onDelete(index);
      }
    });
  };

  const buildDecorators = (fieldName: string) => (config) => {
    if (fieldName === 'defectClassification') {
      return {
        getValueProps: (value) => {
          if (Array.isArray(value)) {
            return {
              value: value.map((val) => {
                if (typeof val === 'string') {
                  return val;
                }
                return val?.value;
              })
            };
          }
          return value;
        },
        getValueFromEvent: (value) => {
          if (Array.isArray(value) && value.length) {
            return config.options.filter((option: any) => {
              return value.includes(option.value);
            });
          }
          return [];
        }
      };
    }
    return {};
  };

  drop(dropRef);

  return (
    <List.Item
      ref={dropRef}
      className={classes.defect}
      colStyle={{
        alignItems: 'start'
      }}
    >
      <Space>
        <div
          hidden={readonly}
          style={{ cursor: 'move' }}
          ref={ref}
          {...collected}
          onMouseOver={() => {
            drag(ref);
            dragPreview(previewRef);
          }}
        >
          <HolderOutlined />
        </div>
        <Space direction="vertical">
          <Button
            hidden={readonly}
            type="text"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.preventDefault();
              onCopy([...fields]);
            }}
          ></Button>
          <Button
            hidden={readonly}
            type="text"
            icon={<DeleteOutlined />}
            danger
            onClick={showWarningPopup}
          ></Button>
        </Space>
      </Space>
      <Row
        ref={previewRef}
        onMouseOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className={classes.defectControls}
        gutter={[8, 8]}
      >
        {fields.map((field, index) => {
          const fieldConfig = config.find(
            ({ fieldName }: any) => fieldName === field.name
          );
          return (
            <Col key={index} flex="auto">
              <Form.Item
                style={{ marginBottom: 0 }}
                label={
                  <Space direction="vertical">
                    <Typography.Text type="secondary">
                      {field.label}
                    </Typography.Text>
                  </Space>
                }
              >
                {field.description && (
                  <Form.Item
                    className={classes.captionItem}
                    style={{ marginBottom: 0 }}
                  >
                    <Typography.Text
                      className={classes.caption}
                      type="secondary"
                    >
                      {field.description}
                    </Typography.Text>
                  </Form.Item>
                )}
                {fieldConfig &&
                  renderInput(
                    {
                      ...fieldConfig,

                      inputProps: {
                        ...fieldConfig.inputProps
                      },
                      readonly
                    },
                    [name, index],
                    `${field.name}-${fieldConfig?.name}`,
                    {
                      style: { paddingLeft: 0 },
                      ...buildDecorators(field.name)(fieldConfig)
                    }
                  )}
              </Form.Item>
            </Col>
          );
        })}
      </Row>
    </List.Item>
  );
};
